import { Component, OnDestroy, OnInit, Injector } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { ChatContextRegistry } from '@client/app/services/chat-context-registry.service';
import {
  AI_CHAT_INSTRUMENTATION_EVENTS,
  ClientInstrumentationEventType,
} from '@shared/interfaces/client-instrumentation.interface';
import { finalize } from 'rxjs/operators';
import { AiChatService, IChatMessagesState } from '../ai-chat.service';
import { TextareaFieldContext } from '../../common-textarea/common-textarea.component';
import { IChatMessageStatus, ISendChatMessageResponse } from '../../../../../shared/interfaces/ai-chat.interface';
import { IHttpErrorResponse } from '../../../interfaces/error.interface';
import { errorMessagesNames } from '../../../../../shared/constants/errors';
import { UserService } from '../../../services/user.service';
import { $user } from '../../../../../shared/constants/user';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { Api } from '../../../../../shared/constants/api';
import { IUserServerResponse } from '../../../../../shared/interfaces/user.interface';
import { BrowserSessionStorageService, SESSION_STORAGE } from '../../../services/browser-session-storage.service';
import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';
import { CommonUtility } from '../../../utility/common.utility';
import { AiChatStatus, IChatContentItem, IChatMessage } from './ai-chat.interface';
import { AiChatInstrumentationService } from '../ai-chat-instrumentation.service';
import { AiChatStateService } from '../ai-chat-state.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import {
  IChatV2StreamEvent,
  SSE_CHAT_V2_EVENT_TYPES,
  SSE_EVENT_TYPES,
} from '../../../../../shared/interfaces/ai-executor.interface';
import { ResponseRating } from '../response-rating.enum';

@Component({
  selector: 'app-ai-chat-base',
  template: ``,
  standalone: false,
})
export class AiChatBaseComponent implements OnInit, OnDestroy {
  private _subscription: Subscription;

  protected readonly IChatMessageStatus: typeof IChatMessageStatus = IChatMessageStatus;
  protected readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  protected readonly AiChatStatus: typeof AiChatStatus = AiChatStatus;

  protected refreshSampleQuestionsRequest$: Subject<void> = new Subject<void>();
  protected focusErrorRequest$: Subject<string | undefined> = new Subject<string | undefined>();
  protected focusInputRequest$: Subject<void> = new Subject<void>();
  protected clearInputRequest$: Subject<void> = new Subject<void>();
  public messages: IChatMessage[] = [];
  public messageValue: string = '';

  protected readonly textAreaContext: TextareaFieldContext;

  protected sessionId!: string;
  public status: AiChatStatus = AiChatStatus.LOADING_SESSION;

  private aiChatService: AiChatService;
  private userService: UserService;
  private browserSessionStorageService: BrowserSessionStorageService;
  private chatContextRegistry: ChatContextRegistry;
  private featureFlagService: FeatureFlagService;

  private aiChatInstrumentationService: AiChatInstrumentationService;
  protected aiChatStateService: AiChatStateService;

  constructor(protected injector: Injector) {
    this.aiChatService = this.injector.get(AiChatService);
    this.userService = this.injector.get(UserService);
    this.browserSessionStorageService = this.injector.get(BrowserSessionStorageService);
    this.chatContextRegistry = this.injector.get(ChatContextRegistry);
    this.featureFlagService = this.injector.get(FeatureFlagService);
    this.aiChatInstrumentationService = this.injector.get(AiChatInstrumentationService);
    this.aiChatStateService = this.injector.get(AiChatStateService);

    this._subscription = new Subscription();
    this.textAreaContext = {
      label: 'ai_chat_input_label',
      rows: 3,
      field: 'message',
      placeholder: 'ai_chat_input_placeholder',
      required: false,
      validateOnSubmitOnly: true,
    };
  }

  public startNewChat(): void {
    this.clearInputRequest$.next();

    if (this.messages.length === 0) {
      this.refreshSampleQuestions();
      return;
    }
    this.doClearChat();
  }

  private doClearChat(): void {
    this.instrumentNewAiChat();
    this.chatContextRegistry.restoreAllContext();

    this.messages = [];
    this.status = AiChatStatus.LOADING_SESSION;

    this.aiChatService.stopPolling();
    this._subscription.unsubscribe();
    this._subscription = new Subscription();

    this.startNewSession(new Date().toISOString());
    this.refreshSampleQuestions();
  }

  private startNewSession(timestamp?: string): void {
    const currentUserProfile: IUserServerResponse = this.userService.retrieveAuthenticatedUserProfile();
    this.createAndStoreSessionId(currentUserProfile, timestamp);
    this.aiChatStateService.setSelectedChatPage(1);
    this.initChatSession();
  }

  private createAndStoreSessionId(userProfile: IUserServerResponse, timestamp?: string): void {
    const sessionTimestamp: string =
      timestamp || (userProfile.lastLogin ? new Date(userProfile.lastLogin).toISOString() : new Date().toISOString());

    this.sessionId = `${Api.chat}-${userProfile[$user.currentTenantId]}-${sessionTimestamp}`;
    this.browserSessionStorageService.setItem(SESSION_STORAGE.AI_CHAT_SESSION_ID, this.sessionId);
  }

  private initChatSession(): void {
    const isStreaming: boolean = this.featureFlagService.variation(FeatureFlagCollection.aiChatStreaming, false);

    if (isStreaming) {
      const messagesSubscription: Subscription = this.aiChatService
        .getMessagesBySessionId(this.sessionId)
        .subscribe((messages: ISendChatMessageResponse[]): void => {
          // API returns messages latest to oldest
          const oldestToLatest = [...messages].reverse();
          const newestMessage: ISendChatMessageResponse | undefined = oldestToLatest.at(-1);
          const isPending: boolean =
            newestMessage?.status === IChatMessageStatus.pending || newestMessage?.status === IChatMessageStatus.processing;

          if (isPending) {
            this.messages = this.mapMessagesToClientChatMessages(oldestToLatest);
            this.sendMessageStream(newestMessage.message, this.messages.length - 1);
            return;
          }

          this.messages = this.mapMessagesToClientChatMessages(oldestToLatest);
          this.status = AiChatStatus.IDLE;
        });
      this._subscription.add(messagesSubscription);
      return;
    }

    const messagesSubscription: Subscription = this.aiChatService
      .getMessagesWithPolling(this.sessionId)
      .subscribe((state: IChatMessagesState): void => {
        this.applyPollingState(state);
      });
    this._subscription.add(messagesSubscription);
  }

  private mapMessagesToClientChatMessages(messages: ISendChatMessageResponse[]): IChatMessage[] {
    return messages.map((message: ISendChatMessageResponse) => {
      let rating = message.rating;

      if (rating && rating > 0 && rating <= 5) {
        rating = rating <= 3 ? ResponseRating.bad : ResponseRating.good;
      }

      const contentBlocks: IChatContentItem[] = message.contentBlocks
        ? (message.contentBlocks as IChatContentItem[])
        : this.buildContentItemsFromResponse(message);

      return {
        ...message,
        rating,
        ratingSubmitted: false,
        contentBlocks,
      };
    });
  }

  // For older chat responses without contentBlocks array
  private buildContentItemsFromResponse(message: ISendChatMessageResponse): IChatContentItem[] {
    if (message.response) {
      return [{ type: 'text', text: message.response }];
    }
    return [];
  }

  private applyPollingState(state: IChatMessagesState): void {
    // API returns messages latest to oldest
    const oldestToLatest = [...state.messages].reverse();
    const clientMessages: IChatMessage[] = this.mapMessagesToClientChatMessages(oldestToLatest);

    if (state.pendingUserMessage) {
      const pendingPlaceholder: IChatMessage = {
        _id: 'temp-message-id',
        message: state.pendingUserMessage,
        response: '',
        userId: null,
        status: IChatMessageStatus.processing,
        createdAt: new Date().toISOString(),
        ratingSubmitted: false,
        events: [],
        contentBlocks: [],
      };
      this.messages = [...clientMessages, pendingPlaceholder];
      this.status = AiChatStatus.WAITING_FOR_STATIC_RESPONSE;
      return;
    }

    this.messages = clientMessages;
    this.status = AiChatStatus.IDLE;
  }

  public onSampleQuestionClick(question: string): void {
    const isPromptInputChangeEnabled = this.featureFlagService.variation(FeatureFlagCollection.aiChatPromptInputChange, false);

    if (!isPromptInputChangeEnabled) {
      this.sendMessage(question);
      return;
    }

    this.messageValue = question;
    this.focusInputRequest$.next();
    setTimeout(() => {
      this.messageValue = '';
    });
  }

  private refreshSampleQuestions(): void {
    this.aiChatStateService.setSuggestionsDisabled(false);
    this.refreshSampleQuestionsRequest$.next();
  }

  /**
   * Get the scroll container element. Override this to return a specific scroll container.
   * Returns null to use window scrolling (default behavior).
   */
  protected getScrollContainer(): HTMLElement | null {
    return null;
  }

  private focusLatestError(messageId?: string): void {
    this.focusErrorRequest$.next(messageId);
  }

  private scrollToMessage(elementId: string): void {
    setTimeout(() => {
      const scrollContainer = this.getScrollContainer();

      if (scrollContainer) {
        // Scroll to bottom of container for chat messages
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth',
        });
      } else {
        // Default: scroll window (for modal version)
        CommonUtility.scrollElementIntoView(elementId, {
          behavior: 'smooth',
          block: 'start',
          inline: 'center',
        });
      }
    });
  }

  private insertOrReplaceMessage(msg: IChatMessage, resetIndex?: number): void {
    if (resetIndex !== undefined) {
      this.messages = [...this.messages.slice(0, resetIndex), msg, ...this.messages.slice(resetIndex + 1)];
    } else {
      this.messages = [...this.messages, msg];
    }
  }

  private sendMessageStream(message: string, resetIndex?: number): void {
    this.status = AiChatStatus.STREAMING_IN_PROGRESS;

    let requestId: string | undefined;
    let streamSessionId: string = this.sessionId ?? '';
    let replaceIdx: number;

    const hasNewMessage = resetIndex === undefined;
    // For now, we insert a new message on retry to reflect API behaviour, where 1 request is exactly 1 message.
    const isErrorRetry = resetIndex !== undefined && this.messages[resetIndex]?.status === IChatMessageStatus.error;

    if (hasNewMessage || isErrorRetry) {
      const id = 'temp-message-id';
      const newMessage: IChatMessage = {
        _id: id,
        message,
        response: '',
        userId: null,
        status: IChatMessageStatus.processing,
        createdAt: new Date().toISOString(),
        ratingSubmitted: false,
        events: [],
        contentBlocks: [],
      };
      this.insertOrReplaceMessage(newMessage);
      this.scrollToMessage(`ai-response-${id}`);
      replaceIdx = this.messages.length - 1;
    } else {
      // stream continuation
      const msg = this.messages[resetIndex];
      this.insertOrReplaceMessage(
        {
          ...msg,
          response: '',
          status: IChatMessageStatus.processing,
          contentBlocks: [],
        },
        resetIndex,
      );
      requestId = msg._id;
      replaceIdx = resetIndex;
    }

    const sendMessageSubscription = this.aiChatService
      .sendMessageStream({
        message,
        sessionId: this.sessionId,
        context: this.chatContextRegistry.getContext(),
        requestId,
      })
      .pipe(
        finalize(() => {
          this.status = AiChatStatus.IDLE;
        }),
      )
      .subscribe({
        next: (event: IChatV2StreamEvent): void => {
          const msg = this.messages[replaceIdx];
          if (event.type === SSE_EVENT_TYPES['executor-start']) {
            streamSessionId = event.data.sessionId;
            this.insertOrReplaceMessage(
              {
                ...msg,
                _id: event.data.requestId,
                sessionId: event.data.sessionId,
              },
              replaceIdx,
            );
            return;
          }

          if (event.type === SSE_EVENT_TYPES['chunk'] && event.data.type === SSE_CHAT_V2_EVENT_TYPES['session-title']) {
            this.aiChatService.sessionTitleUpdated$.next({
              sessionId: streamSessionId,
              title: event.data.title,
            });
            return;
          }
          if (event.type === SSE_EVENT_TYPES['chunk'] && event.data.type === SSE_CHAT_V2_EVENT_TYPES['text']) {
            const items = msg.contentBlocks ?? [];
            const lastItem = items[items.length - 1];
            const updatedItems: IChatContentItem[] =
              lastItem?.type === 'text'
                ? [...items.slice(0, -1), { type: 'text', text: lastItem.text + event.data.text }]
                : [...items, { type: 'text', text: event.data.text }];
            this.insertOrReplaceMessage(
              {
                ...msg,
                response: msg.response + event.data.text,
                contentBlocks: updatedItems,
              },
              replaceIdx,
            );
            return;
          }
          if (event.type === SSE_EVENT_TYPES['chunk'] && event.data.type === SSE_CHAT_V2_EVENT_TYPES['tool-call']) {
            this.insertOrReplaceMessage(
              {
                ...msg,
                events: [
                  ...(msg.events || []),
                  {
                    id: event.data.id,
                    annotation: event.data.annotation,
                  },
                ],
              },
              replaceIdx,
            );
            return;
          }
          if (event.type === SSE_EVENT_TYPES['chunk'] && event.data.type === SSE_CHAT_V2_EVENT_TYPES['ui-component']) {
            this.insertOrReplaceMessage(
              {
                ...msg,
                contentBlocks: [
                  ...(msg.contentBlocks ?? []),
                  { type: 'ui-component', componentType: event.data.componentType, data: event.data.data },
                ],
              },
              replaceIdx,
            );
            return;
          }

          if (event.type === SSE_EVENT_TYPES['streaming-finished']) {
            const { response, contentBlocks } = event.result;
            this.insertOrReplaceMessage(
              {
                ...msg,
                ...(response !== undefined && { response }),
                ...(contentBlocks !== undefined && { contentBlocks }),
              },
              replaceIdx,
            );
            return;
          }

          if (event.type === SSE_EVENT_TYPES['executor-success']) {
            this.insertOrReplaceMessage(
              {
                ...msg,
                status: IChatMessageStatus.done,
              },
              replaceIdx,
            );
            this.instrumentSendQuestion(msg._id);
            return;
          }

          if (event.type === SSE_EVENT_TYPES['executor-failure']) {
            this.insertOrReplaceMessage(
              {
                ...msg,
                status: IChatMessageStatus.error,
                error: event.message,
              },
              replaceIdx,
            );
            this.focusLatestError(msg._id);
            return;
          }
        },
        error: (): void => {
          const msg = this.messages[replaceIdx];
          this.insertOrReplaceMessage(
            {
              ...msg,
              status: IChatMessageStatus.error,
              error: 'ai_response_not_available',
            },
            replaceIdx,
          );
          this.focusLatestError(msg._id);
        },
      });

    this._subscription.add(sendMessageSubscription);
  }

  private sendMessageNonStream(message: string, resetIndex?: number): void {
    let replaceIdx: number;

    const hasNewMessage = resetIndex === undefined;
    // Mirror streaming behaviour: an error retry appends a fresh message rather than overwriting the original error.
    const isErrorRetry = resetIndex !== undefined && this.messages[resetIndex]?.status === IChatMessageStatus.error;

    if (hasNewMessage || isErrorRetry) {
      const id = 'temp-message-id';
      const newMessage: IChatMessage = {
        _id: id,
        message,
        response: '',
        userId: null,
        status: IChatMessageStatus.processing,
        createdAt: new Date().toISOString(),
        ratingSubmitted: false,
        events: [],
        contentBlocks: [],
      };
      this.insertOrReplaceMessage(newMessage);
      this.scrollToMessage(`ai-response-${id}`);
      replaceIdx = this.messages.length - 1;
    } else {
      const msg = this.messages[resetIndex];
      this.insertOrReplaceMessage(
        {
          ...msg,
          status: IChatMessageStatus.processing,
        },
        resetIndex,
      );
      replaceIdx = resetIndex;
    }

    this.status = AiChatStatus.WAITING_FOR_STATIC_RESPONSE;
    const sendMessageSubscription = this.aiChatService
      .sendMessage({
        message,
        sessionId: this.sessionId,
        context: this.chatContextRegistry.getContext(),
      })
      .pipe(
        finalize(() => {
          this.status = AiChatStatus.IDLE;
        }),
      )
      .subscribe({
        next: (response: ISendChatMessageResponse): void => {
          const clientChatMessage = this.mapMessagesToClientChatMessages([response])[0];
          this.insertOrReplaceMessage(clientChatMessage, replaceIdx);
          if (response.status === IChatMessageStatus.error) {
            this.focusLatestError(response._id);
            return;
          }
          if (response._id) {
            this.instrumentSendQuestion(response._id);
          }
        },
        error: (response: IHttpErrorResponse): void => {
          // A raw 504 with no error body may mean the request is still processing upstream — poll for the result.
          // A 504 with a coded error name (e.g. AI_GATEWAY_TIMEOUT) is a definitive response, not one to retry.
          if (response?.status === HttpStatusCode.GatewayTimeout && !response?.error?.app?.name) {
            this.fallbackToPolling(message, replaceIdx);
            return;
          }

          let errorMessage = 'ai_response_not_available';

          if (response?.error?.app?.name === errorMessagesNames.AiGatewayTimeout) {
            errorMessage = 'ai_gateway_timeout';
          } else if (response?.error?.app?.name === errorMessagesNames.AiServiceUnavailable) {
            errorMessage = 'ai_service_unavailable';
          } else if (response?.error?.app?.name === errorMessagesNames.AiRateLimited) {
            errorMessage = 'ai_rate_limited';
          } else if (response?.error?.app?.name === errorMessagesNames.AiChatMessageTooLong) {
            errorMessage = 'ai_chat_message_too_long';
          }

          const msgToReplace = this.messages[replaceIdx];
          const clientChatMsg = this.mapMessagesToClientChatMessages([
            {
              ...msgToReplace,
              status: IChatMessageStatus.error,
              error: errorMessage,
            },
          ])[0];
          this.insertOrReplaceMessage(clientChatMsg, replaceIdx);
          this.focusLatestError(clientChatMsg._id);
        },
      });
    this._subscription.add(sendMessageSubscription);
  }

  /**
   * @param resetIndex is defined during retries
   */
  public sendMessage(message: string, resetIndex?: number): void {
    const isStreaming = this.featureFlagService.variation(FeatureFlagCollection.aiChatStreaming, false);

    if (isStreaming) {
      this.sendMessageStream(message, resetIndex);
      return;
    }

    this.sendMessageNonStream(message, resetIndex);
  }

  private fallbackToPolling(message: string, resetIndex?: number): void {
    const pollingSubscription: Subscription = this.aiChatService.getMessagesWithPolling(this.sessionId).subscribe({
      next: (state: IChatMessagesState): void => {
        this.applyPollingState(state);
      },
      error: (): void => {
        const errorResponse = {
          message,
          status: IChatMessageStatus.error,
          error: 'ai_response_not_available',
        } as ISendChatMessageResponse;
        const targetIndex = resetIndex !== undefined ? resetIndex : this.messages.length - 1;
        this.insertOrReplaceMessage(this.mapMessagesToClientChatMessages([errorResponse])[0], targetIndex);
        this.status = AiChatStatus.IDLE;
        this.focusLatestError();
      },
    });
    this._subscription.add(pollingSubscription);
  }

  public onRetry(message: string, index: number): void {
    this.sendMessage(message, index);
  }

  public onRatingChange(message: IChatMessage, rating: number): void {
    message.rating = rating;
    message.ratingSubmitted = true;

    if (rating === undefined) {
      this._subscription.add(this.aiChatService.unsetRating(message._id, message.sessionId).subscribe());
    } else {
      this._subscription.add(this.aiChatService.setRating(message._id, rating, message.sessionId).subscribe());
      this.instrumentSubmitRating(rating);
    }
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this.aiChatService.stopPolling();
    this.focusErrorRequest$.complete();
    this.focusInputRequest$.complete();
    this.clearInputRequest$.complete();
  }

  public ngOnInit(): void {
    this.aiChatStateService.setSuggestionsDisabled(false);
    this.sessionId = this.browserSessionStorageService.getItem(SESSION_STORAGE.AI_CHAT_SESSION_ID);

    if (SharedCommonUtility.isNullishOrEmpty(this.sessionId)) {
      this.startNewSession();
    } else {
      this.initChatSession();
    }

    this.instrumentOpenAiChat();
  }

  public loadSession(sessionId: string): void {
    this.messages = [];
    this._subscription.unsubscribe();
    this._subscription = new Subscription();
    this.sessionId = sessionId;
    this.browserSessionStorageService.setItem(SESSION_STORAGE.AI_CHAT_SESSION_ID, sessionId);
    this.status = AiChatStatus.LOADING_SESSION;
    this.initChatSession();
    this.focusInputRequest$.next();
  }

  private sendAiChatInstrumentationEvent(
    eventType: ClientInstrumentationEventType,
    additionalProps: Record<string, unknown> = {},
  ): void {
    this.aiChatInstrumentationService.sendEvent(eventType, this.sessionId, additionalProps);
  }

  private instrumentOpenAiChat(): void {
    this.sendAiChatInstrumentationEvent(AI_CHAT_INSTRUMENTATION_EVENTS.OPEN_AI_CHAT);
  }

  private instrumentSendQuestion(messageId: string): void {
    this.sendAiChatInstrumentationEvent(AI_CHAT_INSTRUMENTATION_EVENTS.SEND_QUESTION_AI_CHAT, { messageId });
  }

  private instrumentSubmitRating(rating: number): void {
    this.sendAiChatInstrumentationEvent(AI_CHAT_INSTRUMENTATION_EVENTS.SUBMIT_RATING_AI_CHAT, { rating });
  }

  private instrumentNewAiChat(): void {
    const totalQuestions: number = this.messages.length;
    this.sendAiChatInstrumentationEvent(AI_CHAT_INSTRUMENTATION_EVENTS.NEW_AI_CHAT, { totalQuestions });
  }
}
